import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { defaultsDeep, pick } from 'lodash';
import { catchError, filter, map, merge, of, switchMap, tap } from 'rxjs';

import { ClientContactsActions } from '@lockton-apps/clients/actions';
import {
  ContactCreateUpdateActions,
  ContactsListActions,
} from '@lockton-apps/contacts/action';
import {
  LsosContactActions,
  LsosRenewalsApplicationActions,
} from '@lockton-apps/lsos-renewals/action';
import {
  NorwayProposalActions,
  ProposalShellActions,
} from '@lockton-apps/proposals/action';
import { ClientApiActions } from '@lockton-apps/shared/action/clients';
import { ContactsApiActions } from '@lockton-apps/shared/action/contacts';
import { FormDialiogActions } from '@lockton-apps/shared/action/misc';
import { PoliciesApiActions } from '@lockton-apps/shared/action/policies';
import { AuthSelectors } from '@lockton-apps/shared/data-access/auth';
import {
  ClientRouterSelectors,
  UserRouterSelectors,
} from '@lockton-apps/shared/data-access/router';
import {
  FormDialogCloseResult,
  FormDialogComponent,
  FormDialogData,
} from '@lockton-apps/shared/ui/form-dialog';
import { FeedbackService } from '@lockton-apps/shared/ui/lui';
import { ContactsDialogService } from '@lockton-apps/shared/util/dialogs';

import { ContactsService } from '../services/contacts.service';
import { Role } from '@lockton-apps/shared/interfaces';

const MESSAGES = {
  createSuccess: $localize`Contact added successfully`,
  updateSuccess: $localize`Contact updated successfully`,
  enabledSuccess: $localize`Contact enabled successfully`,
  disabledSuccess: $localize`Contact disabled successfully`,
};

@Injectable()
export class ContactsEffects {
  store = inject(Store);

  loadContactsAsContact$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ContactsListActions.init),
      switchMap(() =>
        this.contactsService
          .all()
          .pipe(
            map((contacts) =>
              ContactsApiActions.loadContactsSuccess({ contacts })
            )
          )
      )
    )
  );

  loadClientContacts$ = createEffect(() =>
    merge(
      this.actions$.pipe(
        ofType(ClientContactsActions.init, ProposalShellActions.init),
        concatLatestFrom(() =>
          this.store.select(ClientRouterSelectors.selectRoutedId)
        ),
        map(([, clientId]) => clientId)
      ),
      this.actions$.pipe(
        ofType(LsosRenewalsApplicationActions.loadClientContacts),
        map(({ clientId }) => clientId)
      ),
      this.actions$.pipe(
        ofType(PoliciesApiActions.loadPolicyForPolicyDetailsSuccess),
        concatLatestFrom(() =>
          this.store.select(AuthSelectors.getIsBasicContact)
        ),
        map(([{ policy }]) => policy.policyClients[0].id)
      )
    ).pipe(
      switchMap((clientId) => {
        if (!clientId) {
          throw new Error('No client id');
        }

        return this.contactsService.allForClient(clientId).pipe(
          map((contacts) =>
            ContactsApiActions.loadClientContactsSuccess({ contacts })
          ),
          catchError((error) =>
            of(ContactsApiActions.loadClientContactsFailure({ error }))
          )
        );
      })
    )
  );

  addContact$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        LsosContactActions.addContact,
        ContactCreateUpdateActions.contactAdded
      ),
      switchMap(({ contact }) =>
        this.contactsService.create(contact).pipe(
          map((res) => ContactsApiActions.addSuccess({ contact: res })),
          catchError((error) => of(ContactsApiActions.addFailure({ error })))
        )
      )
    )
  );

  addClientContactFromClientData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClientApiActions.primaryClientAddedSuccess),
      switchMap(({ client, contact }) =>
        this.contactsService.create({ ...contact, clientId: client.id }).pipe(
          map((contact) =>
            ContactsApiActions.addPrimaryClientContactSuccess({ contact })
          ),
          catchError((error) => of(ContactsApiActions.addFailure({ error })))
        )
      )
    )
  );

  updateContact$ = createEffect(() =>
    merge(
      this.actions$.pipe(
        ofType(ContactCreateUpdateActions.contactUpdated),
        concatLatestFrom(() => [
          this.store.select(UserRouterSelectors.selectRoutedId),
          this.store.select(ClientRouterSelectors.selectQueriedId),
        ]),
        map(([{ contact }, userId, clientId]) => ({
          ...contact,
          clientId,
          userId,
        }))
      ),
      this.actions$.pipe(
        ofType(
          FormDialiogActions.contactUpdated,
          FormDialiogActions.clientAndContactUpdated
        ),
        map(({ contact }) => ({
          ...contact,
          clientId: contact.clients[0].id,
          isPrimary: true,
        }))
      )
    ).pipe(
      switchMap((contact) => {
        return this.contactsService.update(contact, contact.userId).pipe(
          map((contact) =>
            ContactsApiActions.updateContactSuccess({ contact })
          ),
          catchError((error) =>
            of(ContactsApiActions.updateContactFailure({ error }))
          )
        );
      })
    )
  );

  loadContact$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ContactCreateUpdateActions.init),
      concatLatestFrom(() =>
        this.store
          .select(UserRouterSelectors.selectRoutedId)
          .pipe(filter(Boolean))
      ),
      switchMap(([, userId]) => {
        return this.contactsService.load(userId).pipe(
          map((contact) =>
            ContactsApiActions.loadContactSuccess({
              contact,
            })
          ),
          catchError((error) =>
            of(ContactsApiActions.loadContactFailure({ error }))
          )
        );
      })
    )
  );

  enableContact$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        ClientContactsActions.contactEnabled,
        ContactsListActions.contactEnabled
      ),
      switchMap(({ userId }) =>
        this.contactsService.enable(userId).pipe(
          map(() => ContactsApiActions.enabledSuccess({ userId })),
          catchError((error) =>
            of(ContactsApiActions.enabledFailure({ userId, error }))
          )
        )
      )
    )
  );

  checkOpenEnquiries$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        ClientContactsActions.contactDisabled,
        ContactsListActions.contactDisabled
      ),
      switchMap(({ userId }) =>
        this.contactsService.hasOpenEnquiries(userId).pipe(
          map((quoteIds) =>
            ContactsApiActions.hasOpenEnquiriesSuccess({ quoteIds, userId })
          ),
          catchError((error) =>
            of(ContactsApiActions.hasOpenEnquiriesFailure({ userId, error }))
          )
        )
      )
    )
  );

  showDeleteContactRequirements$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ContactsApiActions.hasOpenEnquiriesSuccess),
        filter(({ quoteIds }) => quoteIds.length > 0),
        switchMap(() =>
          this.contactsDialog.showDeleteContactRequirements().pipe(
            filter(Boolean),
            concatLatestFrom(() => [
              this.store.select(AuthSelectors.getRole),
              this.store.select(ClientRouterSelectors.selectRoutedId),
            ]),
            tap(([, role, clientId]) => {
              if (role === Role.Contact) return;
              if (!clientId) {
                console.error(
                  'No client id - You are calling this from an unhandled location'
                );
                return;
              }
              this.router.navigate(['/clients', clientId, 'enquiries']);
            })
          )
        )
      ),
    { dispatch: false }
  );

  disableContact$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ContactsApiActions.hasOpenEnquiriesSuccess),
      filter(({ quoteIds }) => quoteIds.length === 0),
      switchMap(({ userId }) =>
        this.contactsService.disable(userId).pipe(
          map(() => ContactsApiActions.disabledSuccess({ userId })),
          catchError((error) =>
            of(ContactsApiActions.disabledFailure({ userId, error }))
          )
        )
      )
    )
  );

  // Messages

  addSuccessMessage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ContactsApiActions.addSuccess),
        tap(() => {
          this.feedbackService.subtleMessage(MESSAGES.createSuccess, {
            type: 'success',
          });
        })
      ),
    { dispatch: false }
  );

  updateSuccessMessage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ContactsApiActions.updateContactSuccess),
        tap(() => {
          this.feedbackService.subtleMessage(MESSAGES.updateSuccess, {
            type: 'success',
          });
        })
      ),
    { dispatch: false }
  );

  enabledSuccessMessage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ContactsApiActions.enabledSuccess),
        tap(() => {
          this.feedbackService.subtleMessage(MESSAGES.enabledSuccess, {
            type: 'success',
          });
        })
      ),
    { dispatch: false }
  );

  disabledSuccessMessage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ContactsApiActions.disabledSuccess),
        tap(() => {
          this.feedbackService.subtleMessage(MESSAGES.disabledSuccess, {
            type: 'success',
          });
        })
      ),
    { dispatch: false }
  );

  // Navigation

  navigateToClientContacts$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          ContactsApiActions.addSuccess,
          ContactCreateUpdateActions.contactUpdated
        ),
        concatLatestFrom(() =>
          this.store
            .select(ClientRouterSelectors.selectQueriedId)
            .pipe(filter(Boolean))
        ),
        map(([{ contact }, clientId]) => ({ contact, clientId })),
        tap(({ clientId }) => {
          this.router.navigate(['/clients', clientId, 'contacts']);
        })
      ),
    { dispatch: false }
  );

  // Dialogs

  openEditContactDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NorwayProposalActions.editContactClicked),
      switchMap(({ contact }) => {
        return this.dialog
          .open<FormDialogComponent, FormDialogData, FormDialogCloseResult>(
            FormDialogComponent,
            {
              data: {
                title: $localize`Edit Primary Contact`,
                mode: 'edit',
                cancelLabel: $localize`Cancel`,
                confirmLabel: $localize`Save changes`,
                fields: [
                  {
                    fieldGroupClassName: 'vstack gap-1',
                    fieldGroup: [
                      {
                        key: 'givenName',
                        type: 'input',
                        props: { label: $localize`First name`, required: true },
                      },
                      {
                        key: 'surname',
                        type: 'input',
                        props: {
                          label: $localize`Last name`,
                          required: true,
                        },
                      },
                      {
                        key: 'phoneNumber',
                        type: 'number',
                        props: {
                          label: $localize`Phone Number`,
                          required: true,
                          type: 'tel',
                        },
                      },
                      {
                        key: 'additionalFields.dateOfBirth',
                        type: 'datepicker',
                        props: {
                          label: $localize`Date of birth`,
                          required: true,
                          datepickerOptions: {
                            max: 'calc=now|sub|{"years":18}',
                            min: 'calc=now|sub|{"years":120}',
                          },
                        },
                      },
                    ],
                  },
                ],
                model: contact,
              },
            }
          )
          .afterClosed()
          .pipe(
            filter((res) => !!res),
            map((res) => {
              if (!res) {
                throw new Error('No Client');
              }

              const updatedContact = {
                ...res.model,
                displayName: `${res.model.givenName} ${res.model.surname}`,
              };

              return FormDialiogActions.contactUpdated({
                contact: updatedContact,
              });
            })
          );
      })
    )
  );

  openEditIndividualContactDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NorwayProposalActions.editClientAndContactClicked),
      switchMap(({ client, contact }) => {
        console.log(contact);
        return this.dialog
          .open<FormDialogComponent, FormDialogData, FormDialogCloseResult>(
            FormDialogComponent,
            {
              data: {
                title: $localize`Edit Primary Contact`,
                mode: 'edit',
                cancelLabel: $localize`Cancel`,
                confirmLabel: $localize`Save changes`,
                fields: [
                  {
                    fieldGroupClassName: 'vstack gap-1',
                    fieldGroup: [
                      {
                        key: 'contact.givenName',
                        type: 'input',
                        props: { label: $localize`First name`, required: true },
                      },
                      {
                        key: 'contact.surname',
                        type: 'input',
                        props: {
                          label: $localize`Last name`,
                          required: true,
                        },
                      },
                      {
                        key: 'address',
                        type: 'address',
                        props: {
                          label: $localize`Address`,
                          required: true,
                        },
                      },
                      {
                        key: 'contact.phoneNumber',
                        type: 'number',
                        props: {
                          label: $localize`Phone Number`,
                          required: true,
                          type: 'tel',
                        },
                      },
                      {
                        key: 'contact.additionalFields.dateOfBirth',
                        type: 'datepicker',
                        props: {
                          label: $localize`Date of birth`,
                          required: true,
                          datepickerOptions: {
                            max: 'calc=now|sub|{"years":18}',
                            min: 'calc=now|sub|{"years":120}',
                          },
                        },
                      },
                      {
                        key: 'contact.additionalFields.birthNumber',
                        type: 'input',
                        props: {
                          label: $localize`Birth number`,
                        },
                      },
                    ],
                  },
                ],
                model: {
                  address: pick(client.clientData, [
                    'street',
                    'cityTown',
                    'country',
                    'postcode',
                  ]),
                  client,
                  contact,
                },
              },
            }
          )
          .afterClosed()
          .pipe(
            filter((res) => !!res),
            map((res) => {
              if (!res) {
                throw new Error('No Client');
              }

              const updatedClient = defaultsDeep(
                {
                  clientData: {
                    ...res.model.address,
                    insuredName: `${res.model.contact.givenName} ${res.model.contact.surname}`,
                    phoneNumber: res.model.contact.phoneNumber,
                  },
                },
                res.model.client
              );

              const updatedContact = defaultsDeep(
                {
                  displayName: `${res.model.contact.givenName} ${res.model.contact.surname}`,
                },
                res.model.contact
              );

              return FormDialiogActions.clientAndContactUpdated({
                client: updatedClient,
                contact: updatedContact,
              });
            })
          );
      })
    )
  );

  constructor(
    private readonly actions$: Actions,
    private contactsService: ContactsService,
    private router: Router,
    private feedbackService: FeedbackService,
    private dialog: MatDialog,
    private contactsDialog: ContactsDialogService
  ) {}
}
